import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import type { StripPath, Vec2, WallMesh } from '@/core/types';
import { isFound, isLost, measureFraming } from './framing';

/**
 * The 3D viewport.
 *
 * Kept as a plain class rather than a React component tree: the geometry is
 * rebuilt wholesale whenever the design changes, so there is nothing for a
 * reconciler to diff, and a class keeps the render loop and the GPU resources
 * in one place where they can be disposed properly.
 *
 * Lighting is chosen to read relief rather than to look pretty. A low, raking
 * key light casts the long shadows that make a few millimetres of depth
 * visible, and a soft environment keeps the shadowed flanks from going black.
 */

export interface ViewportOptions {
  /** Faceted shading shows each triangle; smooth shading hides the tessellation. */
  flatShading: boolean;
  /** Base colour of the panel material. */
  color: string;
  roughness: number;
  /** Extra spacing between panels, for inspecting joints. Preview only. */
  explode: number;
  showShadows: boolean;
  showJoints: boolean;
  /** Lights the panels from behind, as they would hang with the room lights down. */
  backlight: boolean;
  /**
   * Shows the light-box frames behind the panels. On by default, because a
   * frame is a part of the design: switching backlighting on and seeing the
   * viewport not change at all is worse than any amount of occlusion.
   */
  showFrames: boolean;
  /**
   * Draws the LED strip where it lies inside the boxes, through the panels.
   * A sealed light box shows nothing of its contents from any angle, so
   * without this the only way to see the routing is to pull the wall apart.
   */
  showStrip: boolean;
  backlightColor: string;
  backlightStrength: number;
  /** Key light azimuth in degrees; sweeping it reveals shallow relief. */
  lightAngle: number;
}

export const DEFAULT_VIEWPORT_OPTIONS: ViewportOptions = {
  flatShading: false,
  color: '#d8dee4',
  roughness: 0.62,
  explode: 0,
  showShadows: true,
  showJoints: false,
  backlight: false,
  showFrames: true,
  showStrip: true,
  backlightColor: '#ffd9a0',
  backlightStrength: 2.4,
  lightAngle: 135,
};

/** How long a bad framing has to persist before it is worth saying anything. */
const FRAMING_DWELL_MS = 600;

/** Gap between the back of the assembly and the wall it hangs on, in mm. */
const BACKDROP_CLEARANCE = 0.6;

export class PanelScene {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly controls: OrbitControls;
  private readonly panelGroup = new THREE.Group();
  private readonly frameGroup = new THREE.Group();
  private readonly jointGroup = new THREE.Group();
  private readonly stripGroup = new THREE.Group();
  private readonly keyLight: THREE.DirectionalLight;
  private readonly backdrop: THREE.Mesh;
  private readonly material: THREE.MeshStandardMaterial;
  private readonly frameMaterial: THREE.MeshStandardMaterial;
  private readonly stripMaterial: THREE.MeshBasicMaterial;
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();

  private options: ViewportOptions = { ...DEFAULT_VIEWPORT_OPTIONS };
  // Shared uniform objects, mutated in place so the shader picks changes up
  // without a recompile.
  private readonly glowColor = { value: new THREE.Color(DEFAULT_VIEWPORT_OPTIONS.backlightColor) };
  private readonly glowStrength = { value: 0 };
  private frame = 0;
  private disposed = false;
  private wallSize = new THREE.Vector2(450, 300);
  private frameDepth = 0;
  private readonly wallBox = new THREE.Box3();
  private framed = true;
  private lostSince = 0;

  /** Called with the index of the clicked panel, or `null` for empty space. */
  onSelect: ((index: number | null) => void) | null = null;

  /**
   * Called when the wall leaves, or returns to, the viewport.
   *
   * The camera is never moved on its own: comparing two textures depends on
   * the view staying exactly where it was put, and a viewport that snaps back
   * whenever it disagrees with you is worse than one you have to recover by
   * hand. So this only reports, and the offer to refit is the caller's.
   */
  onFramingChange: ((framed: boolean) => void) | null = null;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    // Deliberately under 1: a relief is read from the difference between lit
    // and shaded facets, and an exposure that clips the highlights erases it.
    this.renderer.toneMappingExposure = 0.92;

    this.camera = new THREE.PerspectiveCamera(38, 1, 1, 8000);
    this.camera.position.set(-260, 180, 620);

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.minDistance = 80;
    this.controls.maxDistance = 3000;
    this.controls.maxPolarAngle = Math.PI * 0.92;

    // A soft studio environment so shadowed flanks keep some shape instead of
    // collapsing to black, which is what kills legibility on shallow relief.
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.environmentIntensity = 0.32;
    pmrem.dispose();

    this.material = new THREE.MeshStandardMaterial({
      color: new THREE.Color(this.options.color),
      roughness: this.options.roughness,
      metalness: 0.02,
      flatShading: false,
    });

    // Standard lighting cannot express light arriving from behind the part, so
    // the per-vertex transmission is injected as extra emissive radiance.
    // Patching the stock material keeps shadows, the environment and tone
    // mapping intact, which a bespoke shader would have thrown away.
    this.material.onBeforeCompile = (shader) => {
      shader.uniforms.uGlowColor = this.glowColor;
      shader.uniforms.uGlowStrength = this.glowStrength;

      shader.vertexShader =
        `attribute float aGlow;\nvarying float vGlow;\n${shader.vertexShader}`.replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\n  vGlow = aGlow;',
        );

      shader.fragmentShader =
        `uniform vec3 uGlowColor;\nuniform float uGlowStrength;\nvarying float vGlow;\n${shader.fragmentShader}`.replace(
          '#include <emissivemap_fragment>',
          '#include <emissivemap_fragment>\n  totalEmissiveRadiance += uGlowColor * vGlow * uGlowStrength;',
        );
    };

    this.keyLight = new THREE.DirectionalLight(0xfff4e8, 2.5);
    this.keyLight.castShadow = true;
    this.keyLight.shadow.mapSize.set(2048, 2048);
    this.keyLight.shadow.bias = -0.0006;
    this.keyLight.shadow.normalBias = 0.6;
    this.scene.add(this.keyLight, this.keyLight.target);

    // Just enough fill to keep the shaded flanks from going to pure black.
    const fill = new THREE.DirectionalLight(0xdae6f5, 0.28);
    fill.position.set(400, -220, 420);
    this.scene.add(fill);

    this.scene.add(new THREE.HemisphereLight(0xdfe8f2, 0x1a1e23, 0.26));

    // Every other light is in front, which is right for reading a relief and
    // leaves the back of the assembly unlit. That is where the light boxes
    // are, with the slots the strip passes through, so orbiting round to look
    // at them found a black sheet. This is only for that side: it faces the
    // wall, so from the front it does little beyond a faint rim.
    const back = new THREE.DirectionalLight(0xe8eef6, 1.15);
    back.position.set(-320, 260, -760);
    this.scene.add(back);

    this.backdrop = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshStandardMaterial({ color: 0x1b1f24, roughness: 0.95, metalness: 0 }),
    );
    this.backdrop.receiveShadow = true;
    this.scene.add(this.backdrop);

    // Frames are a different print in a different filament, so they get their
    // own material rather than being tinted panels.
    //
    // Flat-shaded, always. A frame is a prism with hard printed edges, and
    // averaging normals across the right angle where its floor meets its wall
    // makes the flat floor shade like a dome — from behind, a closed box read
    // as a dark curved sheet rather than as a box.
    this.frameMaterial = new THREE.MeshStandardMaterial({
      color: 0x8e979f,
      roughness: 0.85,
      metalness: 0.02,
      flatShading: true,
    });

    // Unlit and drawn without a depth test, so the run reads through whatever
    // is in front of it. That is the whole point: the strip is sealed inside a
    // box, so an honestly occluded overlay would show nothing at all. Drawing
    // it as an overlay is a deliberate lie about occlusion and about nothing
    // else — the position, the width and the route are the real ones.
    this.stripMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(DEFAULT_VIEWPORT_OPTIONS.backlightColor),
      transparent: true,
      opacity: 0.55,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.stripGroup.renderOrder = 10;

    this.scene.add(this.panelGroup, this.frameGroup, this.jointGroup, this.stripGroup);

    canvas.addEventListener('pointerdown', this.handlePointerDown);
    this.loop();
  }

  private handlePointerDown = (event: PointerEvent): void => {
    if (!this.onSelect) return;

    const rect = this.canvas.getBoundingClientRect();
    this.pointer.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.camera);

    const hit = this.raycaster.intersectObjects(this.panelGroup.children, false)[0];
    this.onSelect(hit ? (hit.object.userData.index as number) : null);
  };

  private loop = (): void => {
    if (this.disposed) return;
    this.frame = requestAnimationFrame(this.loop);
    this.controls.update();
    this.updateFraming();
    this.renderer.render(this.scene, this.camera);
  };

  private updateFraming(): void {
    if (!this.onFramingChange) return;

    const framing = measureFraming(this.wallBox, this.camera);

    if (this.framed) {
      if (!isLost(framing)) {
        this.lostSince = 0;
        return;
      }
      // A pose merely passed through on the way somewhere else should not
      // flash a hint, so a bad framing has to hold before it is reported.
      const now = performance.now();
      if (this.lostSince === 0) {
        this.lostSince = now;
      } else if (now - this.lostSince >= FRAMING_DWELL_MS) {
        this.framed = false;
        this.onFramingChange(false);
      }
    } else if (isFound(framing)) {
      this.lostSince = 0;
      this.framed = true;
      this.onFramingChange(true);
    }
  }

  resize(width: number, height: number): void {
    if (width === 0 || height === 0) return;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  setOptions(options: Partial<ViewportOptions>): void {
    this.options = { ...this.options, ...options };

    this.material.color.set(this.options.color);
    this.material.roughness = this.options.roughness;
    if (this.material.flatShading !== this.options.flatShading) {
      this.material.flatShading = this.options.flatShading;
      this.material.needsUpdate = true;
    }

    this.renderer.shadowMap.enabled = this.options.showShadows;
    this.keyLight.castShadow = this.options.showShadows;
    this.jointGroup.visible = this.options.showJoints;
    this.frameGroup.visible = this.options.showFrames;
    this.stripGroup.visible = this.options.showStrip;
    this.stripMaterial.color.set(this.options.backlightColor);

    this.applyLayout();
    this.glowColor.value.set(this.options.backlightColor);
    this.glowStrength.value = this.options.backlight ? this.options.backlightStrength : 0;
    // Backlighting reads far better against a darker key, the way it would in
    // a room with the lights down.
    this.keyLight.intensity = this.options.backlight ? 0.7 : 2.5;
    this.scene.environmentIntensity = this.options.backlight ? 0.12 : 0.32;

    this.positionLight();
  }

  private positionLight(): void {
    const radius = Math.max(this.wallSize.x, this.wallSize.y) * 1.4 + 200;
    const angle = (this.options.lightAngle * Math.PI) / 180;

    // A low `z` factor keeps the light grazing across the surface. A head-on
    // key light flattens a relief completely: every facet faces it equally and
    // the shadows fall behind the panel where nobody can see them.
    this.keyLight.position.set(
      Math.cos(angle) * radius,
      Math.sin(angle) * radius * 0.8,
      radius * 0.42,
    );
    this.keyLight.target.position.set(0, 0, 0);

    const extent = Math.max(this.wallSize.x, this.wallSize.y) * 0.8 + 60;
    const shadow = this.keyLight.shadow.camera;
    shadow.left = -extent;
    shadow.right = extent;
    shadow.top = extent;
    shadow.bottom = -extent;
    shadow.near = 1;
    shadow.far = radius * 3;
    shadow.updateProjectionMatrix();
  }

  /**
   * Replaces the whole wall. Old geometry is disposed, the material is reused.
   *
   * The joint overlay traces the wall's shared footprint rather than assuming a
   * square, so it is correct for hexagons and triangles too.
   */
  setWall(wall: WallMesh): void {
    this.clearPanels();

    const { width, height } = wall.wall;
    this.wallSize.set(width, height);
    this.frameDepth = wall.frames.length > 0 ? wall.frames[0].bounds.max[2] : 0;

    wall.panels.forEach((panel, index) => {
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(panel.mesh.positions, 3));
      geometry.setIndex(new THREE.BufferAttribute(panel.mesh.indices, 1));
      // Always present, because the shader references it; zero-filled for a
      // panel with no channels so it contributes nothing.
      geometry.setAttribute(
        'aGlow',
        new THREE.BufferAttribute(
          panel.glow ?? new Float32Array(panel.mesh.positions.length / 3),
          1,
        ),
      );
      geometry.computeVertexNormals();
      geometry.computeBoundingSphere();

      const mesh = new THREE.Mesh(geometry, this.material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.index = index;
      mesh.userData.baseX = panel.panel.centerX - width / 2;
      mesh.userData.baseY = panel.panel.centerY - height / 2;
      mesh.userData.baseZ = 0;
      // Panel meshes are built unrotated in their own frame, so the placement
      // rotation is applied here — the same rotation the exporters record.
      mesh.rotation.z = (panel.panel.rotation * Math.PI) / 180;
      this.panelGroup.add(mesh);

      this.addJointOutline(wall.outline, mesh.userData, mesh.rotation.z);
    });

    // Frames sit behind the panels, which is also where they are in the room.
    for (const frame of wall.frames) {
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(frame.mesh.positions, 3));
      geometry.setIndex(new THREE.BufferAttribute(frame.mesh.indices, 1));
      geometry.computeVertexNormals();
      geometry.computeBoundingSphere();

      const mesh = new THREE.Mesh(geometry, this.frameMaterial);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.baseX = frame.panel.centerX - width / 2;
      mesh.userData.baseY = frame.panel.centerY - height / 2;
      mesh.userData.baseZ = -frame.bounds.max[2];
      mesh.rotation.z = (frame.panel.rotation * Math.PI) / 180;
      this.frameGroup.add(mesh);
    }

    this.buildStrip(wall, width, height);

    // Large enough to stay behind the wall at any reasonable zoom, so its edge
    // never reads as a floating board.
    const backdropSize = Math.max(width, height, 400) * 5;
    this.backdrop.scale.set(backdropSize, backdropSize, 1);
    this.applyLayout();
    this.positionLight();
  }

  /**
   * Places every part from its stored base position.
   *
   * Separated from `setWall` because both the explode spread and the assembly
   * separation are display options, not design changes: applying them only on
   * a rebuild meant dragging the Explode slider did nothing at all until
   * something else happened to invalidate the mesh.
   */
  private applyLayout(): void {
    const spread = 1 + this.options.explode;

    // Exploding an assembly means pulling it apart in depth as well as across,
    // so the same control that spreads the panels also lifts them off their
    // frames. Tying the separation to frame *visibility* instead, as this did
    // before, conflated "can I see the part" with "how is it arranged".
    const separation = this.options.explode * this.frameDepth * 3;

    // A hair of clearance even when nothing is exploded: the frame's rim and
    // the panel's back face are otherwise exactly coplanar and z-fight.
    const contact = 0.08;

    for (const group of [this.panelGroup, this.jointGroup, this.frameGroup, this.stripGroup]) {
      // The strip lies inside a box, so it travels with the boxes.
      const isFrame = group === this.frameGroup || group === this.stripGroup;
      for (const child of group.children) {
        const base = child.userData;
        if (typeof base.baseX !== 'number') continue;
        child.position.set(
          base.baseX * spread,
          base.baseY * spread,
          base.baseZ + (isFrame ? -separation - contact : separation) + (base.zBias ?? 0),
        );
      }
    }

    // The backdrop is the room wall the assembly hangs on, so it belongs behind
    // the deepest part of it. Pinned just behind the panel instead, as it was,
    // it cut straight through the light boxes: a box is 30-odd millimetres deep
    // and stands off the wall, not sunk into it. From the front that is
    // invisible, which is why it survived. From behind it hid every frame and
    // left a dark sheet with the panels' own shadows on it where the slots for
    // the strip should have been.
    this.backdrop.position.z = -(this.frameDepth + separation + contact) - BACKDROP_CLEARANCE;

    // Recomputed here rather than per frame, because this is the only place the
    // parts move. Frames count whether or not they are shown, so that hiding
    // them does not change what "the wall is off screen" means.
    this.wallBox.makeEmpty();
    this.wallBox.expandByObject(this.panelGroup);
    this.wallBox.expandByObject(this.frameGroup);
  }

  /**
   * A flat ribbon for each run, at its real width, lying on the box floor.
   *
   * One mesh per box rather than one for the wall, so the existing layout pass
   * spreads the strip with its own panel when the assembly is exploded instead
   * of leaving it hanging in the gap.
   */
  private buildStrip(wall: WallMesh, width: number, height: number): void {
    if (wall.strip.length === 0 || !wall.backlight) return;

    const halfWidth = wall.backlight.stripWidthMm / 2;
    // The floor top sits one air gap behind the panel's back; the strip lies
    // on it, lifted just enough to read as sitting on the floor rather than in
    // it.
    const z = -wall.backlight.airGapMm + 0.5;

    const byPanel = new Map<number, StripPath[]>();
    for (const path of wall.strip) {
      const bucket = byPanel.get(path.panel);
      if (bucket) bucket.push(path);
      else byPanel.set(path.panel, [path]);
    }

    for (const [index, paths] of byPanel) {
      const panel = wall.panels[index]?.panel ?? wall.frames[index]?.panel;
      if (!panel) continue;

      const positions: number[] = [];
      const indices: number[] = [];

      for (const path of paths) {
        const count = path.closed ? path.points.length : path.points.length - 1;
        for (let i = 0; i < count; i += 1) {
          const from = path.points[i];
          const to = path.points[(i + 1) % path.points.length];
          const dx = to.x - from.x;
          const dy = to.y - from.y;
          const length = Math.hypot(dx, dy);
          if (length < 1e-6) continue;

          const ux = dx / length;
          const uy = dy / length;
          // A closed loop is mitred crudely, by running each edge half a width
          // long at both ends; the overlap fills the corner. An open run ends
          // at the box wall, where overrunning would push it through.
          const overrun = path.closed ? halfWidth : 0;
          const ax = from.x - ux * overrun;
          const ay = from.y - uy * overrun;
          const bx = to.x + ux * overrun;
          const by = to.y + uy * overrun;
          const nx = -uy * halfWidth;
          const ny = ux * halfWidth;

          const base = positions.length / 3;
          // Vertices are offsets from the box centre, so the layout pass can
          // move the whole thing by setting a position.
          for (const [px, py] of [
            [ax + nx, ay + ny],
            [bx + nx, by + ny],
            [bx - nx, by - ny],
            [ax - nx, ay - ny],
          ]) {
            positions.push(px - panel.centerX, py - panel.centerY, 0);
          }
          indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
        }
      }

      if (indices.length === 0) continue;

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setIndex(indices);
      geometry.computeBoundingSphere();

      const mesh = new THREE.Mesh(geometry, this.stripMaterial);
      mesh.userData.baseX = panel.centerX - width / 2;
      mesh.userData.baseY = panel.centerY - height / 2;
      mesh.userData.baseZ = z;
      this.stripGroup.add(mesh);
    }
  }

  /** Thin wireframe outlines tracing each panel's footprint. */
  private addJointOutline(
    outline: readonly Vec2[],
    base: { baseX?: unknown; baseY?: unknown; baseZ?: unknown },
    rotation: number,
  ): void {
    if (!outline || outline.length < 3) return;

    const points = outline.map((point) => new THREE.Vector3(point.x, point.y, 0));
    points.push(points[0].clone());

    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(points),
      new THREE.LineBasicMaterial({ color: 0x68d8a8, transparent: true, opacity: 0.65 }),
    );
    line.userData.baseX = base.baseX;
    line.userData.baseY = base.baseY;
    line.userData.baseZ = base.baseZ;
    // Just clear of the panel surface, so the outline is not z-fighting it.
    line.userData.zBias = 0.05;
    line.rotation.z = rotation;
    this.jointGroup.add(line);
  }

  private clearPanels(): void {
    for (const child of this.panelGroup.children) {
      (child as THREE.Mesh).geometry.dispose();
    }
    this.panelGroup.clear();

    for (const child of this.frameGroup.children) {
      (child as THREE.Mesh).geometry.dispose();
    }
    this.frameGroup.clear();

    for (const child of this.jointGroup.children) {
      const line = child as THREE.Line;
      line.geometry.dispose();
      (line.material as THREE.Material).dispose();
    }
    this.jointGroup.clear();

    // The strip material is shared, so only the geometry is this group's to
    // release.
    for (const child of this.stripGroup.children) {
      (child as THREE.Mesh).geometry.dispose();
    }
    this.stripGroup.clear();
  }

  /**
   * Frames the whole wall.
   *
   * Fitting both axes matters: a wide wall in a tall viewport is limited by the
   * horizontal field of view, which is the vertical one scaled by the aspect
   * ratio, and fitting only the height would push it off the sides.
   */
  fitView(): void {
    const halfFov = (this.camera.fov * Math.PI) / 360;
    const vertical = this.wallSize.y / 2 / Math.tan(halfFov);
    const horizontal = this.wallSize.x / 2 / (Math.tan(halfFov) * this.camera.aspect);
    // The extra 1.22 covers the relief depth and the oblique viewing angle.
    const distance = Math.max(vertical, horizontal, 60) * 1.22;

    // A three-quarter view from above left: the same angle a wall panel is seen
    // from in a room, and the one that shows its shadows.
    const direction = new THREE.Vector3(-0.42, 0.26, 1).normalize();

    this.controls.target.set(0, 0, 0);
    this.camera.position.copy(direction.multiplyScalar(distance));
    this.controls.update();

    // A fit frames the wall by construction, so say so now rather than a frame
    // later: the hint that prompted it should go away on the click, not after.
    this.lostSince = 0;
    if (!this.framed) {
      this.framed = true;
      this.onFramingChange?.(true);
    }
  }

  /** Diagonal of the current wall, used to decide when a refit is warranted. */
  get wallDiagonal(): number {
    return Math.hypot(this.wallSize.x, this.wallSize.y);
  }

  /** Matches the backdrop to the page so the panels read as mounted on a wall. */
  setBackground(color: string): void {
    (this.backdrop.material as THREE.MeshStandardMaterial).color.set(color);
  }

  /** Renders the current view to a PNG data URL, for sharing a design. */
  captureImage(): string {
    this.renderer.render(this.scene, this.camera);
    return this.renderer.domElement.toDataURL('image/png');
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.frame);
    this.canvas.removeEventListener('pointerdown', this.handlePointerDown);

    this.clearPanels();
    this.material.dispose();
    this.frameMaterial.dispose();
    this.stripMaterial.dispose();
    this.backdrop.geometry.dispose();
    (this.backdrop.material as THREE.Material).dispose();
    this.scene.environment?.dispose();
    this.controls.dispose();
    this.renderer.dispose();
  }
}
